/**
 * The quick-edit studio's pure logic: how the creator's actions change the
 * edit list (trim at the playhead, cut a stretch out, add a logo or a line of
 * text, change speed), undo and redo, and the words the screen shows for it.
 * No React here, so it is tested as plain functions.
 */

import {
  type Anchor,
  type EditList,
  type Overlay,
  type Position,
  type Segment,
  type TextOverlay,
  type TextStyle,
  type Voiceover,
  EDIT_LIMITS,
  outputDurationMs,
  outputTimeAt,
  playedSegments,
  segmentPlacements,
  sourceTimeAt,
} from '../../utils/editing/editList';

export const MIN_SEGMENT_MS = EDIT_LIMITS.minSegmentMs;

let counter = 0;
/** Short unique ids for new items (the API only needs them unique within the list). */
export const newItemId = (prefix: string): string => {
  counter += 1;
  return `${prefix}-${Date.now().toString(36)}${counter.toString(36)}`;
};

/** The explicit segments of a list: the whole source when it had none. */
export const explicitSegments = (list: EditList): Segment[] =>
  playedSegments(list).map((segment) => (segment.id === 'all' ? { ...segment, id: newItemId('seg') } : segment));

/** Splits the stretch playing at an output time into two, at that moment. */
export function splitAt(list: EditList, outputMs: number): EditList {
  const placements = segmentPlacements(list);
  const hit = placements.find((p) => outputMs > p.startMs && outputMs < p.endMs);
  if (!hit) return list;
  const sourceMs = Math.round(hit.segment.inMs + (outputMs - hit.startMs) * hit.segment.speed);
  if (sourceMs - hit.segment.inMs < MIN_SEGMENT_MS || hit.segment.outMs - sourceMs < MIN_SEGMENT_MS) return list;
  const segments = explicitSegments(list);
  const index = placements.indexOf(hit);
  const first = { ...segments[index], outMs: sourceMs };
  const second = { ...segments[index], id: newItemId('seg'), inMs: sourceMs };
  return { ...list, segments: [...segments.slice(0, index), first, second, ...segments.slice(index + 1)] };
}

/** Removes a stretch of the result (output times): what was outside it closes up. */
export function removeRange(list: EditList, fromMs: number, toMs: number): EditList {
  if (toMs <= fromMs) return list;
  const from = Math.max(0, fromMs);
  const to = Math.min(outputDurationMs(list), toMs);
  const kept: Segment[] = [];
  segmentPlacements(list).forEach(({ segment, startMs, endMs }, index) => {
    const id = segment.id === 'all' ? `seg-${index}` : segment.id;
    const before = Math.min(endMs, from);
    const after = Math.max(startMs, to);
    if (before - startMs >= MIN_SEGMENT_MS * (1 / segment.speed) && before > startMs) {
      kept.push({ ...segment, id, outMs: Math.round(segment.inMs + (before - startMs) * segment.speed) });
    }
    if (endMs - after >= MIN_SEGMENT_MS * (1 / segment.speed) && after < endMs) {
      kept.push({ ...segment, id: before > startMs ? newItemId('seg') : id, inMs: Math.round(segment.inMs + (after - startMs) * segment.speed) });
    }
  });
  // Everything that was placed later moves up with the cut.
  const shift = (ms: number): number => (ms <= from ? ms : ms >= to ? ms - (to - from) : from);
  return shiftPlaced({ ...list, segments: kept.filter((s) => s.outMs - s.inMs >= MIN_SEGMENT_MS) }, shift);
}

/** Keeps only one stretch of the result (a Short): everything else goes. */
export function keepOnly(list: EditList, fromMs: number, toMs: number): EditList {
  const total = outputDurationMs(list);
  let next = list;
  if (toMs < total) next = removeRange(next, toMs, total);
  if (fromMs > 0) next = removeRange(next, 0, fromMs);
  return next;
}

/** Moves every placed item's times through `shift` and drops what lands at zero length. */
function shiftPlaced(list: EditList, shift: (ms: number) => number): EditList {
  const total = outputDurationMs(list);
  const clamp = (ms: number) => Math.max(0, Math.min(total, ms));
  const overlays = list.overlays
    .map((overlay) => ({ ...overlay, fromMs: overlay.fromMs === null ? null : clamp(shift(overlay.fromMs)), toMs: overlay.toMs === null ? null : clamp(shift(overlay.toMs)) }))
    .filter((overlay) => overlay.fromMs === null || overlay.toMs === null || overlay.toMs > overlay.fromMs);
  const texts = list.texts
    .map((text) => ({ ...text, fromMs: clamp(shift(text.fromMs)), toMs: clamp(shift(text.toMs)) }))
    .filter((text) => text.toMs > text.fromMs);
  const voiceovers = list.voiceovers.map((voiceover) => ({ ...voiceover, atMs: clamp(shift(voiceover.atMs)) }));
  const music = list.music ? { ...list.music, startMs: clamp(shift(list.music.startMs)) } : null;
  return { ...list, overlays, texts, voiceovers, music };
}

/**
 * After the result got shorter (a speed change, a tighter stretch): anything placed
 * past the new end slides back so it still shows, keeping its length where it can.
 */
function fitPlaced(list: EditList): EditList {
  const total = outputDurationMs(list);
  const fit = (fromMs: number, toMs: number): { fromMs: number; toMs: number } => {
    if (toMs <= total) return { fromMs, toMs };
    const length = toMs - fromMs;
    return { fromMs: Math.max(0, total - length), toMs: total };
  };
  const overlays = list.overlays.map((overlay) => {
    if (overlay.toMs === null) return { ...overlay, fromMs: overlay.fromMs === null ? null : Math.min(overlay.fromMs, Math.max(0, total - 1000)) };
    const fitted = fit(overlay.fromMs ?? 0, overlay.toMs);
    return { ...overlay, fromMs: overlay.fromMs === null ? null : fitted.fromMs, toMs: fitted.toMs };
  }).filter((overlay) => overlay.toMs === null || overlay.fromMs === null || overlay.toMs > overlay.fromMs);
  const texts = list.texts.map((text) => ({ ...text, ...fit(text.fromMs, text.toMs) })).filter((text) => text.toMs > text.fromMs);
  const voiceovers = list.voiceovers.map((voiceover) => ({ ...voiceover, atMs: Math.min(voiceover.atMs, Math.max(0, total - 1)) }));
  const music = list.music ? { ...list.music, startMs: Math.min(list.music.startMs, Math.max(0, total - 1)) } : null;
  return { ...list, overlays, texts, voiceovers, music };
}

/** Changes one segment's speed; what was placed past the new end slides back in. */
export function setSegmentSpeed(list: EditList, segmentId: string, speed: number): EditList {
  const segments = explicitSegments(list).map((segment) => (segment.id === segmentId ? { ...segment, speed } : segment));
  return fitPlaced({ ...list, segments });
}

export function setSegmentBounds(list: EditList, segmentId: string, inMs: number, outMs: number): EditList {
  const segments = explicitSegments(list).map((segment) => (segment.id === segmentId ? { ...segment, inMs: Math.round(inMs), outMs: Math.round(outMs) } : segment));
  if (segments.some((s) => s.outMs - s.inMs < MIN_SEGMENT_MS || s.inMs < 0 || s.outMs > list.sourceDurationMs)) return list;
  return fitPlaced({ ...list, segments });
}

export function removeSegment(list: EditList, segmentId: string): EditList {
  const placements = segmentPlacements(list);
  const hit = placements.find((p) => p.segment.id === segmentId);
  if (!hit || placements.length === 1) return list;
  return removeRange(list, hit.startMs, hit.endMs);
}

export function moveSegment(list: EditList, segmentId: string, direction: -1 | 1): EditList {
  const segments = explicitSegments(list);
  const index = segments.findIndex((segment) => segment.id === segmentId);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= segments.length) return list;
  const reordered = segments.slice();
  reordered[index] = segments[target];
  reordered[target] = segments[index];
  return { ...list, segments: reordered };
}

export const defaultPosition = (anchor: Anchor, marginPct = 3): Position => ({ anchor, marginPct, xPct: null, yPct: null });

export function addOverlay(list: EditList, assetId: string, position: Position = defaultPosition('top-right')): EditList {
  if (list.overlays.length >= EDIT_LIMITS.maxOverlays) return list;
  const overlay: Overlay = { id: newItemId('pic'), assetId, position, widthPct: 15, opacity: 1, fromMs: null, toMs: null };
  return { ...list, overlays: [...list.overlays, overlay] };
}

export function addText(list: EditList, style: TextStyle, text: string, atMs: number, options: { secondaryText?: string | null; color?: string; durationMs?: number } = {}): EditList {
  if (list.texts.length >= EDIT_LIMITS.maxTexts) return list;
  const total = outputDurationMs(list);
  const fromMs = Math.max(0, Math.min(Math.round(atMs), Math.max(0, total - 1000)));
  const toMs = Math.min(total, fromMs + (options.durationMs ?? (style === 'title' ? 3000 : 5000)));
  const defaults: Record<TextStyle, { position: Position; sizePct: number }> = {
    title: { position: defaultPosition('center', 0), sizePct: 8 },
    lowerThird: { position: defaultPosition('bottom-left', 8), sizePct: 5 },
    plain: { position: defaultPosition('bottom', 8), sizePct: 5 },
  };
  const item: TextOverlay = {
    id: newItemId('txt'),
    text,
    secondaryText: options.secondaryText || null,
    style,
    font: 'sans',
    position: defaults[style].position,
    sizePct: defaults[style].sizePct,
    color: options.color || '#FFFFFF',
    backgroundColor: style === 'lowerThird' ? '#000000A0' : null,
    fadeMs: style === 'plain' ? 0 : 300,
    fromMs,
    toMs,
  };
  return { ...list, texts: [...list.texts, item] };
}

export function addVoiceover(list: EditList, assetId: string, atMs: number): EditList {
  if (list.voiceovers.length >= EDIT_LIMITS.maxVoiceovers) return list;
  const item: Voiceover = { id: newItemId('vo'), source: { kind: 'asset', assetId }, atMs: Math.max(0, Math.round(atMs)), gainDb: 0, duckOriginal: true };
  return { ...list, voiceovers: [...list.voiceovers, item] };
}

export function updateItem<T extends { id: string }>(items: T[], id: string, change: Partial<T>): T[] {
  return items.map((item) => (item.id === id ? { ...item, ...change } : item));
}

export function removeItem<T extends { id: string }>(items: T[], id: string): T[] {
  return items.filter((item) => item.id !== id);
}

// ---------------------------------------------------------------------------
// Undo
// ---------------------------------------------------------------------------

export interface History {
  past: EditList[];
  present: EditList;
  future: EditList[];
}

const MAX_HISTORY = 100;

export const historyOf = (list: EditList): History => ({ past: [], present: list, future: [] });

export function commit(history: History, next: EditList): History {
  if (next === history.present) return history;
  return { past: [...history.past.slice(-MAX_HISTORY + 1), history.present], present: next, future: [] };
}

export function undo(history: History): History {
  if (history.past.length === 0) return history;
  const previous = history.past[history.past.length - 1];
  return { past: history.past.slice(0, -1), present: previous, future: [history.present, ...history.future] };
}

export function redo(history: History): History {
  if (history.future.length === 0) return history;
  const [next, ...rest] = history.future;
  return { past: [...history.past, history.present], present: next, future: rest };
}

/** Replaces the present after a save brought back the server's canonical copy; history stays. */
export const replacePresent = (history: History, list: EditList): History => ({ ...history, present: list });

// ---------------------------------------------------------------------------
// Words and numbers for the screen
// ---------------------------------------------------------------------------

export const formatMs = (ms: number): string => {
  const total = Math.max(0, Math.round(ms));
  const tenths = Math.floor((total % 1000) / 100);
  const seconds = Math.floor(total / 1000) % 60;
  const minutes = Math.floor(total / 60000) % 60;
  const hours = Math.floor(total / 3600000);
  const mmss = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${tenths}`;
  return hours > 0 ? `${hours}:${mmss}` : mmss;
};

export const formatMinutes = (minutes: number): string => {
  if (minutes >= 60) return `${Math.round(minutes / 6) / 10} h`;
  return `${Math.round(minutes * 10) / 10} min`;
};

/** The source time the preview should show for an output time, clamped into the result. */
export const previewSourceMs = (list: EditList, outputMs: number): number => {
  const total = outputDurationMs(list);
  const clamped = Math.max(0, Math.min(total, outputMs));
  const sourceMs = sourceTimeAt(list, clamped);
  return sourceMs ?? playedSegments(list)[0].inMs;
};

export const previewOutputMs = (list: EditList, sourceMs: number): number | null => outputTimeAt(list, sourceMs);

export const ANCHOR_LABELS: Record<Anchor, string> = {
  'top-left': 'Top left', top: 'Top', 'top-right': 'Top right',
  left: 'Left', center: 'Centre', right: 'Right',
  'bottom-left': 'Bottom left', bottom: 'Bottom', 'bottom-right': 'Bottom right',
};

export const RESOLUTION_LABELS: Record<string, string> = { '720p': '720p', '1080p': '1080p (Full HD)', '2160p': '4K' };
export const ASPECT_LABELS: Record<string, string> = { source: 'As recorded', '16:9': '16:9 (YouTube)', '9:16': '9:16 (Shorts, Reels)', '1:1': '1:1 (square)', '4:5': '4:5 (feed)' };
